import React, { FunctionComponent, createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { makePost } from "@culturehq/client";

import { ElementState, Preview } from "@creatomate/preview";
import { Loader } from "@culturehq/components";

import { AWSLanguage, Dimensions, ElementSource, MediaVersion, Story, Subtitle, Template } from "chq";
import { mockStory } from "testFactories";

import { useSignUpload } from "app/hooks";
import { getSubdomain, isPlatformSubdomain } from "app/utils";
import { useProfile } from "app/context";
import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import { PostStoryState } from "../../post-stories-v2/types";
import { IMAGE_TYPE, VIDEO_TYPE } from "../utils/constants";
import VideoStudioModal from "../VideoStudioModal";
import CreatomatePlayer from "../player/CreatomatePlayer";
import { TimeProvider } from "./TimeContext";
import { TimelineScaleProvider } from "./TimelineScaleContext";
import { hasBurnSubtitlesOn } from "../sidebar/tabs/subtitle/utils";

interface CustomButtonProps {
  onClick: () => void;
}

export type CustomButtonType = FunctionComponent<CustomButtonProps>;

interface VideoStudioContextProps {
  playerCreatomateJson?: ElementSource;
  CustomButton?: CustomButtonType;
  dimensions?: Dimensions;
  disabled?: boolean;
  isLastMediaVersionProcessing?: boolean;
  justIcon?: boolean;
  lastMediaVersion?: MediaVersion;
  mediaVersionHistory?: MediaVersion[];
  mode?: "player" | "interactive";
  montage?: boolean;
  onMediaEdited?: (value: ElementSource, thumbnail: PostStoryState["thumbnail"]) => void;
  onPauseVideo?: (value?: boolean) => void;
  onPosted?: (story: Story) => void;
  onStoryUpdate?: (story: Story) => void;
  onThirdPartyMediasReload?: (value: boolean) => void;
  onUpdateDraftVersion?: (draftVersion: MediaVersion) => void;
  onAnotherModalOpen?: (value: boolean) => void;
  pausedVideo?: boolean;
  stories?: Story[];
  story?: Story;
  storyQuestionId?: number;
  thirdPartyMediaIsFetching?: boolean;
  trend?: string;
  templateId?: string;
}

export interface CreatomateData {
  activeElementsId: string[];
  activeMenuItem: string;
  aspectRatioStringValue: string;
  aspectRatio?: number;
  blockSubtitles: boolean;
  burnSubtitles: boolean;
  creatomateJson?: ElementSource;
  currentSubtitleId?: string;
  dimensions?: Dimensions;
  elements: ElementState[];
  error?: string;
  fillColor?: string;
  isPlaying: boolean;
  isLastMediaVersionProcessing?: boolean;
  loading: boolean;
  mediaType: string;
  mediaVersionHistory?: MediaVersion[];
  lastMediaVersion?: MediaVersion;
  montage: boolean;
  onMediaEdited?: (value: ElementSource, thumbnail: PostStoryState["thumbnail"]) => void;
  onPosted?: (story: Story) => void;
  onStoryUpdate?: (story: Story) => void;
  onThirdPartyMediasReload?: (value: boolean) => void;
  onAnotherModalOpen?: (value: boolean) => void;
  pendingSubtitle?: AWSLanguage;
  progress: number;
  replaceVideo?: ElementSource;
  selectedMediaVersion?: MediaVersion;
  selectedTemplate?: Template;
  selectedThumbnail?: Blob | string;
  stories?: Story[];
  story?: Story;
  storyQuestionId?: number;
  subtitlesOn: boolean;
  thirdPartyMediaIsFetching?: boolean;
  updateSubtitles?: boolean;
  uploadingMedia?: boolean;
  trend?: string;
  autosave: {
    isAutosaving: boolean;
    done?: undefined | boolean;
    error?: undefined | string;
  };
  templateId?: string;
}

type AutosaveDraftParams = {
  creatomateJson?: ElementSource;
  burnSubtitles?: boolean;
  selectedThumbnail?: Blob;
  selectedMediaVersion?: MediaVersion;
};

export interface CreatomateContextType {
  data: CreatomateData;
  getActiveElement: (preview: Preview) => ElementState | undefined;
  handleUpdateData: (newData: ElementSource) => void;
  handleDebouncedAutosaveDraft: (params: AutosaveDraftParams) => void;
  handleAutosaveDraft: (params: AutosaveDraftParams) => Promise<void>;
  getBurnSubtitles: (version: MediaVersion) => boolean;
  restartData: (noUpdate?: boolean) => void;
}

const baseContextData = {
  activeElementsId: [],
  activeMenuItem: "media",
  aspectRatioStringValue: "wide-landscape",
  blockSubtitles: false,
  burnSubtitles: false,
  dimensions: { width: 1280, height: 720 },
  elements: [],
  isPlaying: false,
  loading: false,
  mediaType: VIDEO_TYPE,
  mediaVersionHistory: [],
  montage: false,
  onMediaEdited: (_value: ElementSource) => { },
  progress: 0,
  replacingVideo: false,
  selectedTemplate: undefined,
  subtitlesOn: false,
  thirdPartyMediaIsFetching: false,
  updateSubtitles: false,
  uploadingMedia: false,
  autosave: {
    isAutosaving: false,
    done: undefined,
    error: undefined
  }
};

export const CreatomateContext = createContext<CreatomateContextType>({
  data: {
    ...baseContextData,
    story: mockStory()
  },
  getActiveElement: () => undefined,
  handleUpdateData: (_newData: ElementSource) => { },
  handleDebouncedAutosaveDraft: (_params: AutosaveDraftParams) => { },
  handleAutosaveDraft: async (_params: AutosaveDraftParams) => { },
  getBurnSubtitles: (_version: MediaVersion) => false,
  restartData: () => { }
});

const VideoStudioContext: React.FC<VideoStudioContextProps> = ({
  playerCreatomateJson,
  dimensions,
  isLastMediaVersionProcessing,
  onMediaEdited,
  onPauseVideo,
  onPosted,
  onStoryUpdate,
  onThirdPartyMediasReload,
  onUpdateDraftVersion,
  pausedVideo,
  mediaVersionHistory,
  montage = false,
  stories,
  story,
  storyQuestionId,
  thirdPartyMediaIsFetching,
  trend,
  mode,
  onAnotherModalOpen,
  templateId,
  ...props
}) => {
  const { profile: { organization } } = useProfile();
  const signUpload = useSignUpload();
  const { templateBuilderSelection } = useTemplateBuilder();
  const previewRef = useRef<Preview>();
  const [data, setData] = useState<CreatomateData>({ ...baseContextData, story });
  const isPlayer = mode === "player";

  const handleUpdateData = (newData: Record<string, any>) => {
    setData((prevData: CreatomateData) => ({ ...prevData, ...newData }));
  };

  const { selectedMediaVersion, lastMediaVersion } = useMemo(() => (
    {
      selectedMediaVersion: mediaVersionHistory?.[0] || {
        additionalData: {
          output_format: templateBuilderSelection === "image" || story?.media?.mediaType === IMAGE_TYPE ? "png" : "mp4"
        }
      },
      lastMediaVersion: props.lastMediaVersion || mediaVersionHistory?.find(
        version => !version.draft && !version.original)
    } as { selectedMediaVersion: MediaVersion; lastMediaVersion: MediaVersion }
    // eslint-disable-next-line react-hooks/exhaustive-deps, react/destructuring-assignment
  ), [mediaVersionHistory, templateBuilderSelection, props.lastMediaVersion]);

  const getBurnSubtitles = (
    { burnSubtitles }: MediaVersion
  ) => burnSubtitles ?? (story?.media?.burnSubtitles || hasBurnSubtitlesOn(organization.name));

  const dataRef = useRef<CreatomateData>(data);
  dataRef.current = data;

  const initialDataRef = useRef<CreatomateData>(data);
  const initialData = useMemo(
    () => {
      const { additionalData } = selectedMediaVersion;
      let defaultData = {
        ...data,
        activeMenuItem: montage ? "stories" : data.activeMenuItem,
        burnSubtitles: getBurnSubtitles(selectedMediaVersion as MediaVersion),
        creatomateJson: additionalData,
        dimensions,
        isLastMediaVersionProcessing,
        mediaVersionHistory,
        onMediaEdited,
        onPosted,
        onStoryUpdate,
        onThirdPartyMediasReload,
        onAnotherModalOpen,
        montage,
        thirdPartyMediaIsFetching,
        selectedMediaVersion,
        lastMediaVersion,
        stories,
        story,
        storyQuestionId,
        subtitlesOn: !!data.pendingSubtitle,
        templateId,
        trend
      };
      // Validation if the story exists and it is an image or
      // the localfile is an image or it is an Unsplash image
      if (story?.media?.mediaType === IMAGE_TYPE
        || (story?.media as unknown as File || undefined)?.type?.startsWith(IMAGE_TYPE)
        || story?.media?.urls
        || (!!additionalData?.output_format && additionalData.output_format !== "mp4")) {
        defaultData = { ...defaultData, mediaType: IMAGE_TYPE };
      } else {
        defaultData = { ...defaultData, mediaType: VIDEO_TYPE };
      }
      return defaultData;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mediaVersionHistory,
      selectedMediaVersion,
      lastMediaVersion,
      story,
      thirdPartyMediaIsFetching,
      trend,
      templateBuilderSelection,
      templateId
    ]
  );
  initialDataRef.current = initialData;

  useEffect(
    () => {
      setData(initialDataRef.current);
    }, [initialData, mode]
  );

  const handleAutosaveDraft = async ({
    creatomateJson,
    burnSubtitles,
    selectedThumbnail,
    selectedMediaVersion: newSelectedMediaVersion
  }: AutosaveDraftParams) => {
    if (!story?.media?.id || !selectedMediaVersion?.thirdPartyRenderId) return;

    const mediaId = story?.media?.id as number;

    const creatomateDraftParams = await (async () => {
      const params = {} as any;

      if (burnSubtitles !== undefined) params.burnSubtitles = burnSubtitles;

      if (creatomateJson?.elements?.length) {
        const hasElementTempUrl = !!creatomateJson.elements.find(
          (e: ElementSource) => (["audio", "video", "image"].includes(e.source?.type))
            && e.source?.source
            && isPlatformSubdomain(getSubdomain(e.source.source))
        );
        if (hasElementTempUrl) return params;

        const creatomateJsonPayload = {
          ...creatomateJson,
          ...creatomateJson.source,
          elements: creatomateJson.elements
            .map((e: ElementSource) => e.source)
            .filter((e: ElementSource) => !!e)
        };
        delete creatomateJsonPayload.source;
        params.creatomateJson = JSON.stringify(creatomateJsonPayload);
      }

      const thumbnail = selectedThumbnail && await signUpload(selectedThumbnail);
      if (thumbnail) params.thumbnail = thumbnail;

      return params;
    })();

    const hasCreatomateDraftParams = !!Object.keys(creatomateDraftParams).length;

    const updateState = (updatedAt = new Date()) => {
      const promises: any[] = [];
      setData((prev: CreatomateData) => {
        const burnSubtitles = creatomateDraftParams.burnSubtitles ?? prev.burnSubtitles;
        const { thumbnail } = creatomateDraftParams;

        const mediaVersion = newSelectedMediaVersion || prev.selectedMediaVersion;
        const updatedSelectedMediaVersion = {
          ...mediaVersion,
          burnSubtitles,
          thumbnail: thumbnail
            ?? mediaVersion?.thumbnail
            ?? mediaVersion?.metadata?.thumbnail
        } as any;

        if (onUpdateDraftVersion) onUpdateDraftVersion(updatedSelectedMediaVersion);

        return {
          ...prev,
          burnSubtitles,
          selectedMediaVersion: updatedSelectedMediaVersion,
          selectedThumbnail: thumbnail ?? prev.selectedThumbnail,
          autosave: {
            ...prev.autosave,
            isAutosaving: false,
            done: true,
            error: undefined,
            updatedAt
          }
        };
      });
      return Promise.all(promises);
    };

    if (!hasCreatomateDraftParams) return;

    setData(
      (prev: CreatomateData) => ({
        ...prev,
        autosave: {
          ...prev.autosave,
          isAutosaving: true,
          done: undefined,
          error: undefined
        }
      })
    );

    await makePost("/creatomate_drafts", { ...creatomateDraftParams, mediaId })
      .then(({ updatedAt }: any) => updateState(updatedAt))
      .catch(_err => {
        setData(
          (prev: CreatomateData) => ({
            ...prev,
            autosave: {
              ...prev.autosave,
              isAutosaving: false,
              done: false,
              error: "Error autosaving"
            }
          })
        );
      });
  };
  const handleDebouncedAutosaveDraft = useCallback(
    debounce(handleAutosaveDraft, 3000), [selectedMediaVersion?.thirdPartyRenderId]);

  const restartData = (noUpdate = false) => {
    if (!noUpdate && onAnotherModalOpen) {
      onAnotherModalOpen(false);
    }
    setData({
      ...initialDataRef.current,
      blockSubtitles: false,
      activeMenuItem: montage ? "stories" : "media",
      selectedTemplate: undefined,
      creatomateJson: undefined,
      elements: []
    });
  };

  const getActiveElement = (preview: Preview) => {
    if (!preview || data.activeElementsId.length === 0) return undefined;

    const id = data.activeElementsId[0];
    return preview.findElement((element: ElementState) => element.source.id === id, preview.state);
  };

  if (thirdPartyMediaIsFetching) {
    if (isPlayer) return <Loader loading />;
    return <></>;
  }

  return (
    <CreatomateContext.Provider value={{
      data,
      getActiveElement,
      handleUpdateData,
      handleDebouncedAutosaveDraft,
      handleAutosaveDraft,
      getBurnSubtitles,
      restartData
    }}
    >
      <TimeProvider>
        <TimelineScaleProvider>
          {isPlayer
            ? (
              <CreatomatePlayer
                creatomateJson={playerCreatomateJson}
                mode={mode}
                onPauseVideo={onPauseVideo}
                pausedVideo={pausedVideo}
                previewRef={previewRef}
              />
            ) : (
              <VideoStudioModal
                dimensions={dimensions}
                montage={montage}
                onMediaEdited={onMediaEdited}
                onPauseVideo={onPauseVideo}
                previewRef={previewRef}
                stories={stories}
                {...props}
              />
            )}
        </TimelineScaleProvider>
      </TimeProvider>
    </CreatomateContext.Provider>
  );
};

export default VideoStudioContext;
